import React, { useState, useEffect } from 'react';

interface ExternalData {
  source: string;
  pdb_id?: string;
  title?: string;
  experimental_method?: string;
  release_date?: string;
  uniprot_id?: string;
  protein_name?: string;
  organism?: string;
  length?: number;
  matches?: Array<{
    uniprot_id: string;
    entry_name: string;
    protein_name: string;
    organism: string;
  }>;
}

interface ActionableLink {
  label: string;
  url: string;
  type: 'external' | 'internal';
}

interface ExternalLookupResult {
  query: string;
  search_type: string;
  found_external: boolean;
  suggestions: string[];
  external_data: ExternalData | null;
  recommendation: string;
  actionable_links: ActionableLink[];
}

interface NotFoundSuggestionsProps {
  query: string;
  searchType: string;
  onRetry: () => void;
  onBrowse: () => void;
}

interface SuggestionResult {
  success: boolean;
  message: string;
  suggestion_id?: string;
  estimated_review_time?: string;
}

const NotFoundSuggestions: React.FC<NotFoundSuggestionsProps> = ({
  query,
  searchType,
  onRetry,
  onBrowse
}) => {
  const [externalData, setExternalData] = useState<ExternalLookupResult | null>(null);
  const [similarEntries, setSimilarEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSuggestionForm, setShowSuggestionForm] = useState(false);
  const [suggestionSubmitted, setSuggestionSubmitted] = useState<SuggestionResult | null>(null);
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);

  useEffect(() => {
    const fetchExternalData = async () => {
      if (!query) {
        setLoading(false);
        return;
      }

      try {
        const backendUrl = '';
        
        const params = new URLSearchParams({
          query: query,
          ...(searchType && { search_type: searchType })
        });
        
        const response = await fetch(`${backendUrl}/external/lookup?${params}`);
        
        if (response.ok) {
          const data = await response.json();
          setExternalData(data);
        }

        // Also try to get similar entries from our database using partial search
        try {
          // First try the similar search endpoint if it exists
          let similarData = null;
          try {
            const similarParams = new URLSearchParams({
              query: query,
              limit: '3',
              search_type: searchType
            });
            
            const similarResponse = await fetch(`${backendUrl}/search/similar?${similarParams}`);
            
            if (similarResponse.ok) {
              similarData = await similarResponse.json();
            }
          } catch (e) {
          }
          
          // Fallback to partial search using main search endpoint
          if (!similarData || !similarData.entries || similarData.entries.length === 0) {
            // Try partial matches by using broader search terms
            let partialQuery = query;
            if (searchType === 'pdb_id' && query.length >= 2) {
              partialQuery = query.substring(0, 2); // Search with first 2 characters
            } else if (searchType === 'protein_name' && query.length >= 3) {
              partialQuery = query.substring(0, Math.floor(query.length * 0.7)); // Search with 70% of query
            }
            
            if (partialQuery !== query && partialQuery.length >= 2) {
              const partialParams = new URLSearchParams({
                protein_name: partialQuery,
                per_page: '3'
              });
              
              const partialResponse = await fetch(`${backendUrl}/search?${partialParams}`);
              
              if (partialResponse.ok) {
                const partialData = await partialResponse.json();
                if (partialData.entries && partialData.entries.length > 0) {
                  setSimilarEntries(partialData.entries);
                }
              }
            }
          } else if (similarData.entries && similarData.entries.length > 0) {
            setSimilarEntries(similarData.entries);
          }
        } catch (error) {
          console.warn('Similar entries lookup failed:', error);
        }
      } catch (error) {
        console.warn('External lookup failed:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchExternalData();
  }, [query, searchType]);

  const handleSuggestionSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmittingSuggestion(true);

    try {
      const formData = new FormData(event.currentTarget);
      const backendUrl = '';
      
      const response = await fetch(`${backendUrl}/suggest-protein`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setSuggestionSubmitted(result);
        setShowSuggestionForm(false);
      } else {
        setSuggestionSubmitted({
          success: false,
          message: 'Failed to submit suggestion. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      setSuggestionSubmitted({
        success: false,
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  const handleLinkClick = (link: ActionableLink) => {
    if (link.type === 'external') {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else if (link.url === '#browse') {
      onBrowse();
    }
  };

  const getSearchTypeIcon = (type: string) => {
    switch (type) {
      case 'pdb_id':
        return '🧬';
      case 'uniprot_id':
        return '🔬';
      case 'protein_abbreviation':
      case 'protein_name':
        return '🧪';
      default:
        return '🔍';
    }
  };

  const getSearchTypeLabel = (type: string) => {
    switch (type) {
      case 'pdb_id':
        return 'PDB ID';
      case 'uniprot_id':
        return 'UniProt ID';
      case 'protein_abbreviation':
        return 'Protein Abbreviation';
      case 'protein_name':
        return 'Protein Name';
      default:
        return 'Search Query';
    }
  };

  return (
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: '40px',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{
          fontSize: '64px',
          marginBottom: '16px',
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          🔍
        </div>
        <h2 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          marginBottom: '8px'
        }}>
          Not Found in STINGExoDB
        </h2>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          backgroundColor: '#f1f5f9',
          padding: '8px 16px',
          borderRadius: '12px',
          fontSize: '14px',
          color: '#475569',
          marginBottom: '16px'
        }}>
          <span>{getSearchTypeIcon(searchType)}</span>
          <span><strong>{getSearchTypeLabel(searchType)}:</strong> "{query}"</span>
        </div>
        <p style={{
          fontSize: '16px',
          color: '#64748b',
          lineHeight: '1.5',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          {searchType === 'pdb_id' 
            ? `The PDB ID "${query}" is not currently available in our curated exosite database entries. This structure may exist in the Protein Data Bank but hasn't been curated for exosite information yet.`
            : searchType === 'uniprot_id'
            ? `The UniProt ID "${query}" is not found in our current database entries. This protein may exist in UniProt but hasn't been curated for exosite information yet.`
            : searchType === 'protein_name' || searchType === 'protein_abbreviation'
            ? `The protein "${query}" is not found in our current database entries. We may not have curated this protein for exosite information yet, or it might be listed under a different name.`
            : `"${query}" was not found in our current database entries. It may not have been curated for exosite information yet.`
          }
        </p>
      </div>

      {/* External Data Section */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #bfdbfe',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#64748b' }}>Searching external databases...</p>
        </div>
      ) : externalData?.found_external ? (
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            backgroundColor: '#ecfdf5',
            border: '1px solid #10b981',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{
                backgroundColor: '#10b981',
                borderRadius: '50%',
                padding: '8px',
                color: 'white',
                fontSize: '16px',
                lineHeight: '1'
              }}>
                ✓
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#065f46',
                  marginBottom: '8px'
                }}>
                  Found in {externalData.external_data?.source}!
                </h3>
                <p style={{
                  color: '#047857',
                  marginBottom: '16px',
                  lineHeight: '1.5'
                }}>
                  {externalData.recommendation}
                </p>

                {/* Display external data based on type */}
                {externalData.external_data?.source === 'RCSB PDB' && (
                  <div style={{
                    backgroundColor: 'white',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #d1fae5'
                  }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#065f46' }}>
                      PDB Structure Information
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                      <div><strong>PDB ID:</strong> {externalData.external_data.pdb_id}</div>
                      <div><strong>Method:</strong> {externalData.external_data.experimental_method}</div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <strong>Title:</strong> {externalData.external_data.title}
                      </div>
                    </div>
                  </div>
                )}

                {externalData.external_data?.source === 'UniProt' && (
                  <div style={{
                    backgroundColor: 'white',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #d1fae5'
                  }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#065f46' }}>
                      UniProt Entry Information
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                      <div><strong>UniProt ID:</strong> {externalData.external_data.uniprot_id}</div>
                      <div><strong>Organism:</strong> {externalData.external_data.organism}</div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <strong>Protein Name:</strong> {externalData.external_data.protein_name}
                      </div>
                    </div>
                  </div>
                )}

                {externalData.external_data?.matches && (
                  <div style={{
                    backgroundColor: 'white',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid #d1fae5'
                  }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#065f46' }}>
                      Potential Matches in UniProt
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {externalData.external_data.matches.slice(0, 3).map((match, index) => (
                        <div key={index} style={{
                          padding: '12px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}>
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            {match.uniprot_id} - {match.entry_name}
                          </div>
                          <div style={{ color: '#64748b' }}>
                            {match.protein_name} ({match.organism})
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Similar Entries Section */}
      {similarEntries.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #0ea5e9',
            borderRadius: '12px',
            padding: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                backgroundColor: '#0ea5e9',
                borderRadius: '50%',
                padding: '8px',
                color: 'white',
                fontSize: '16px',
                lineHeight: '1'
              }}>
                💡
              </div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#0c4a6e',
                margin: 0
              }}>
                Found Similar Entries - Is this what you're looking for?
              </h3>
            </div>
            <p style={{
              color: '#075985',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              We found {similarEntries.length} similar entr{similarEntries.length === 1 ? 'y' : 'ies'} in our database that might match your search:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {similarEntries.map((entry, index) => {
                const isIndividual = entry.entry_type === 'individual';
                const displayData = isIndividual ? entry.individual_entry : entry.grouped_entry;
                const proteinName = displayData?.protein_name || 'Unknown';
                const pdbIds = isIndividual 
                  ? (displayData?.pdb_id || '').split(',').map((id: string) => id.trim()).filter((id: string) => id)
                  : (displayData?.representative_pdb_ids || []);
                
                return (
                  <div key={index} style={{
                    padding: '16px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #bae6fd',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                    e.currentTarget.style.borderColor = '#0ea5e9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.borderColor = '#bae6fd';
                  }}
                  onClick={() => {
                    // Trigger a new search for this entry
                    window.location.href = `#search=${encodeURIComponent(proteinName)}`;
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1e293b',
                          margin: '0 0 4px 0'
                        }}>
                          {proteinName}
                        </h4>
                        <div style={{
                          fontSize: '14px',
                          color: '#64748b',
                          marginBottom: '8px'
                        }}>
                          {displayData?.organism && (
                            <span><strong>Organism:</strong> {displayData.organism} • </span>
                          )}
                          <span><strong>Type:</strong> {isIndividual ? 'Individual Entry' : 'Grouped Entry'}</span>
                        </div>
                        {pdbIds.length > 0 && (
                          <div style={{ fontSize: '12px', color: '#475569' }}>
                            <strong>PDB IDs:</strong> {pdbIds.slice(0, 3).join(', ')}
                            {pdbIds.length > 3 && ` (+${pdbIds.length - 3} more)`}
                          </div>
                        )}
                      </div>
                      <div style={{
                        backgroundColor: '#0ea5e9',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        View Entry
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Suggestions Section */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#1e293b',
          marginBottom: '16px'
        }}>
          What can you do next?
        </h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {(externalData?.suggestions || (() => {
            if (searchType === 'pdb_id') {
              return [
                `Verify the PDB ID "${query}" is spelled correctly (e.g., 1ABC, 2XYZ)`,
                "Try searching for the protein name instead if you know it",
                "Browse our database to see what structures are available",
                "Check if this is a recent PDB entry that hasn't been curated yet"
              ];
            } else if (searchType === 'uniprot_id') {
              return [
                `Verify the UniProt ID "${query}" is correct (format: P12345 or A0A0B4J2F2)`,
                "Try searching with the protein name instead",
                "Browse similar organisms or protein families in our database",
                "Check if this protein has alternative UniProt entries"
              ];
            } else if (searchType === 'protein_name' || searchType === 'protein_abbreviation') {
              return [
                `Try alternative names or abbreviations for "${query}"`,
                "Search using common synonyms or the full protein name",
                "Check for different species variants of this protein",
                "Browse proteins by organism or functional category",
                "Try searching with associated PDB or UniProt IDs if available"
              ];
            } else {
              return [
                "Double-check the spelling and format of your query",
                "Try searching with alternative names or identifiers",
                "Browse our database by categories to find related proteins"
              ];
            }
          })()).map((suggestion, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '16px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{
                backgroundColor: '#3b82f6',
                borderRadius: '50%',
                color: 'white',
                fontSize: '12px',
                fontWeight: '600',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {index + 1}
              </div>
              <p style={{ color: '#475569', margin: 0, lineHeight: '1.5' }}>
                {suggestion}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Suggestion Submission Success/Error */}
      {suggestionSubmitted && (
        <div style={{
          backgroundColor: suggestionSubmitted.success ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${suggestionSubmitted.success ? '#10b981' : '#ef4444'}`,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{
              backgroundColor: suggestionSubmitted.success ? '#10b981' : '#ef4444',
              borderRadius: '50%',
              padding: '8px',
              color: 'white',
              fontSize: '16px',
              lineHeight: '1'
            }}>
              {suggestionSubmitted.success ? '✓' : '✗'}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{
                color: suggestionSubmitted.success ? '#047857' : '#dc2626',
                margin: '0 0 8px 0',
                fontWeight: '600'
              }}>
                {suggestionSubmitted.success ? 'Suggestion Submitted!' : 'Submission Failed'}
              </p>
              <p style={{
                color: suggestionSubmitted.success ? '#065f46' : '#b91c1c',
                margin: 0,
                fontSize: '14px'
              }}>
                {suggestionSubmitted.message}
              </p>
              {suggestionSubmitted.success && suggestionSubmitted.suggestion_id && (
                <div style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}>
                  <strong>Reference ID:</strong> {suggestionSubmitted.suggestion_id}
                  {suggestionSubmitted.estimated_review_time && (
                    <><br/><strong>Estimated Review Time:</strong> {suggestionSubmitted.estimated_review_time}</>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Suggestion Form */}
      {showSuggestionForm && !suggestionSubmitted && (
        <div style={{
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '16px'
          }}>
            Suggest "{query}" for Addition
          </h3>
          <form onSubmit={handleSuggestionSubmit}>
            <input type="hidden" name="protein_query" value={query} />
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '6px' 
              }}>
                Contact Email (optional)
              </label>
              <input
                type="email"
                name="contact_email"
                placeholder="your@email.com"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '6px' 
              }}>
                Additional Information (optional)
              </label>
              <textarea
                name="additional_info"
                placeholder="Any additional context about this protein, related papers, or why it should be included..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="submit"
                disabled={submittingSuggestion}
                style={{
                  padding: '10px 20px',
                  backgroundColor: submittingSuggestion ? '#9ca3af' : '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: submittingSuggestion ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {submittingSuggestion ? 'Submitting...' : 'Submit Suggestion'}
              </button>
              <button
                type="button"
                onClick={() => setShowSuggestionForm(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        justifyContent: 'center'
      }}>
        {/* External Links */}
        {externalData?.actionable_links?.map((link, index) => (
          <button
            key={index}
            onClick={() => handleLinkClick(link)}
            style={{
              padding: '12px 24px',
              backgroundColor: link.type === 'external' ? '#3b82f6' : '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = link.type === 'external' ? '#2563eb' : '#059669';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = link.type === 'external' ? '#3b82f6' : '#10b981';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {link.label}
            {link.type === 'external' && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/>
              </svg>
            )}
          </button>
        ))}
        
        {/* Default Action Buttons */}
        <button
          onClick={onBrowse}
          style={{
            padding: '12px 24px',
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#059669';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#10b981';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Browse Database
        </button>
        
        {/* Suggest for Addition Button */}
        {!showSuggestionForm && !suggestionSubmitted && (
          <button
            onClick={() => setShowSuggestionForm(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#d97706';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f59e0b';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span>💡</span>
            Suggest for Addition
          </button>
        )}
        
        <button
          onClick={onRetry}
          style={{
            padding: '12px 24px',
            backgroundColor: 'white',
            color: '#374151',
            border: '2px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#9ca3af';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#d1d5db';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Try Another Search
        </button>
      </div>

      {/* Add spinning animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default NotFoundSuggestions;